import { Color } from '@opallabs/gem'
import { getNestedProperty } from '@opallabs/lambda'
import { AssignmentStatus } from '@opallabs/satellite/src/AssignmentHelpers/types'
import {
  ContentCollection,
  MomentCollection,
  PhaseCollection,
  PhaseItem,
  ServiceCollection,
} from '../../../types'
import { decorateServiceName } from '../../jsonApiParser/decorators'
import { NormalizedTask } from './types'

type PhaseItemToTaskMapper = (arg: {
  phaseItems: PhaseItem[]
  phases: PhaseCollection
  contents: ContentCollection
  moments: MomentCollection
  services: ServiceCollection
}) => NormalizedTask[]

export const normalizePhaseItemsToTasks: PhaseItemToTaskMapper = ({
  phaseItems,
  phases,
  contents,
  moments,
  services,
}) =>
  phaseItems.map((phaseItem) => {
    const taskCompleted = phaseItem.status === 'complete'

    const normalizedTask: NormalizedTask = {
      checked: taskCompleted,
      completedAt: taskCompleted ? new Date(phaseItem.updated_at) : null,
      checkpointableDate: new Date(phaseItem.created_at),
      disableCheckbox: false,
      iconColor: Color.light.Text.Gray,
      iconName: '', // use decorateServiceName when merged
      id: phaseItem.id,
      hardDueDate: false,
      contextName: '',
      taskText: phaseItem.request ? phaseItem.request : '',
      starrable: phaseItem.status !== 'complete',
      starred: phaseItem.is_starred ? phaseItem.is_starred : false,
      taskStatus: AssignmentStatus.Default,
      phaseIsComplete: false,
      phaseIsCurrent: false,
      entityType: 'phase_item',
      assignmentType: 'task',
    }

    let dueDateSet = false

    // determine attributes that rely on phase information
    const phaseId = getNestedProperty<string>(
      phaseItem,
      'relationships',
      'phase',
      'id'
    )
    if (phaseId !== undefined) {
      const phase = phases[phaseId]

      // determine phaseIsComplete and phaseIsCurrent
      normalizedTask.phaseIsComplete = phase.is_complete
        ? phase.is_complete
        : false
      normalizedTask.phaseIsCurrent = phase.is_current
        ? phase.is_current
        : false

      // determine hardDueDate
      if (phase.due_date !== null) {
        normalizedTask.dueDate = new Date(phase.due_date)
        normalizedTask.hardDueDate = true
        dueDateSet = true
      }
    }

    // determine attributes that rely on context/moment/service information
    const contentId = getNestedProperty<string>(
      phaseItem,
      'relationships',
      'approvable',
      'id'
    )
    if (contentId !== undefined) {
      const content = contents[contentId]
      normalizedTask.contentId = contentId

      const momentId = getNestedProperty<string>(
        content,
        'relationships',
        'moment',
        'id'
      )
      if (momentId !== undefined) {
        normalizedTask.momentId = momentId
        const moment = moments[momentId]
        if (moment.scheduled_at) {
          normalizedTask.contextLiveDate = new Date(moment.scheduled_at)
          if (!dueDateSet) {
            normalizedTask.dueDate = new Date(moment.scheduled_at)
          }
        }
      }

      const serviceId = getNestedProperty<string>(
        content,
        'relationships',
        'service',
        'id'
      )
      if (serviceId !== undefined) {
        const service = services[serviceId]
        normalizedTask.iconColor = service.color
        normalizedTask.iconName = decorateServiceName(
          service.system_name,
          'star',
          true,
          true
        )
        normalizedTask.contextName = content.name
      }
    }

    // determine disableCheckbox
    if (
      (normalizedTask.checked && normalizedTask.phaseIsComplete) ||
      (!normalizedTask.phaseIsCurrent && !normalizedTask.phaseIsComplete)
    ) {
      // if the task was completed and the phase along with it, we can't undo the task
      // if the task phase is neither current nor completed, that means it is a future stage and cannot be interacted with
      normalizedTask.disableCheckbox = true
    }

    return normalizedTask
  })
